// Servicio para manejar el envío de correos electrónicos
// Utiliza una función serverless de Netlify para evitar problemas de compatibilidad con el navegador

import { EMAIL_CONFIG, VALIDATION_CONFIG, getEnvironmentConfig } from '../config/emailConfig.js';
import { recordEmailSuccess, recordEmailFailure, withMetrics } from '../utils/emailMetrics.js';

/**
 * Valida una dirección de email
 * @param {string} email - Email a validar
 * @returns {boolean} - Indica si el email es válido
 */
const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  return VALIDATION_CONFIG.EMAIL_REGEX.test(email.trim());
};

/**
 * Valida los parámetros de entrada para envío de email
 * @param {string} to - Destinatario
 * @param {string} subject - Asunto
 * @param {string} content - Contenido
 * @returns {Object} - Resultado de validación
 */
const validateEmailParams = (to, subject, content) => {
  const errors = [];
  
  if (!isValidEmail(to)) {
    errors.push('Email destinatario inválido');
  }
  
  if (!subject || typeof subject !== 'string' || subject.trim().length === 0) {
    errors.push('Asunto requerido');
  } else if (subject.length > EMAIL_CONFIG.MAX_SUBJECT_LENGTH) {
    errors.push(`Asunto excede ${EMAIL_CONFIG.MAX_SUBJECT_LENGTH} caracteres`);
  }
  
  if (!content || typeof content !== 'string' || content.trim().length === 0) {
    errors.push('Contenido requerido');
  } else if (content.length > EMAIL_CONFIG.MAX_CONTENT_LENGTH) {
    errors.push(`Contenido excede ${EMAIL_CONFIG.MAX_CONTENT_LENGTH} caracteres`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Verifica si las variables de entorno necesarias están configuradas
 * @returns {Object} - Resultado de verificación con detalles
 */
const checkEmailConfig = () => {
  const email = import.meta.env.VITE_EMAIL_USER;
  const errors = [];
  
  if (!email) {
    errors.push('VITE_EMAIL_USER no configurado');
  } else if (!isValidEmail(email)) {
    errors.push('VITE_EMAIL_USER no es un email válido');
  }
  
  const result = {
    isValid: errors.length === 0,
    errors,
    config: { email }
  };
  
  if (!result.isValid) {
    console.error('❌ Configuración de email inválida:', { errors });
  }
  
  return result;
};

/**
 * Inicializa el servicio de correo electrónico
 * @returns {Object} - Resultado de inicialización con detalles
 */
export const initEmailService = () => {
  try {
    const configCheck = checkEmailConfig();
    
    if (!configCheck.isValid) {
      return {
        success: false,
        errors: configCheck.errors,
        message: 'Configuración de email inválida'
      };
    }
    
    console.log('✅ Servicio de correo inicializado correctamente', {
      timestamp: new Date().toISOString(),
      config: configCheck.config
    });
    
    return {
      success: true,
      message: 'Servicio inicializado correctamente'
    };
  } catch (error) {
    console.error('❌ Error al inicializar servicio de correo:', {
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
    
    return {
      success: false,
      errors: [error.message],
      message: 'Error durante inicialización'
    };
  }
};

/**
 * Función auxiliar para esperar un tiempo determinado
 * @param {number} ms - Milisegundos a esperar
 * @returns {Promise} - Promesa que se resuelve después del tiempo especificado
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Envía un correo electrónico con reintentos automáticos
 * @param {string} to - Destinatario del correo
 * @param {string} subject - Asunto del correo
 * @param {string} html - Contenido HTML del correo
 * @param {Object} options - Opciones adicionales
 * @returns {Promise<Object>} - Resultado del envío con detalles
 */
export const sendEmail = withMetrics(async (to, subject, html, options = {}) => {
  const startTime = Date.now();
  const requestId = Math.random().toString(36).substr(2, 9);
  const envConfig = getEnvironmentConfig();
  
  // Log detallado de parámetros recibidos para depuración
  console.log('📧 Parámetros recibidos en sendEmail:', {
    requestId,
    to: { value: to, type: typeof to, isString: typeof to === 'string' },
    subject: { value: subject, type: typeof subject, isString: typeof subject === 'string' },
    html: { value: html ? html.substring(0, 100) + '...' : html, type: typeof html, isString: typeof html === 'string', length: html ? html.length : 0 },
    timestamp: new Date().toISOString()
  });
  
  // Log estructurado del inicio
  console.log('📧 Iniciando envío de email:', {
    requestId,
    to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***', // Ocultar email por privacidad
    subject: (typeof subject === 'string' ? subject.substring(0, 50) : String(subject || '').substring(0, 50)),
    environment: envConfig.environment,
    timestamp: new Date().toISOString()
  });
  
  // Validar parámetros de entrada
  const validation = validateEmailParams(to, subject, html);
  if (!validation.isValid) {
    const error = {
      success: false,
      errors: validation.errors,
      message: 'Parámetros de email inválidos',
      requestId,
      duration: Date.now() - startTime,
      receivedParams: { to, subject, html: html ? `${html.length} caracteres` : html }
    };
    console.error('❌ Validación de email fallida:', error);
    return error;
  }
  
  // Verificar configuración
  const configCheck = checkEmailConfig();
  if (!configCheck.isValid) {
    const error = {
      success: false,
      errors: configCheck.errors,
      message: 'Configuración de email inválida',
      requestId,
      duration: Date.now() - startTime
    };
    console.error('❌ Configuración de email inválida:', error);
    return error;
  }
  
  // En desarrollo, simular el envío exitoso
  if (envConfig.emailSimulation) {
    console.log('📧 [MODO DESARROLLO] Simulando envío de correo:', {
      requestId,
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      subject,
      contentLength: html.length,
      environment: envConfig.environment,
      timestamp: new Date().toISOString()
    });
    
    // Simular un pequeño delay
    await sleep(EMAIL_CONFIG.DEV_SIMULATION_DELAY);
    
    const result = {
      success: true,
      message: 'Correo simulado enviado exitosamente',
      requestId,
      duration: Date.now() - startTime,
      mode: 'development'
    };
    
    console.log('✅ Correo simulado enviado:', result);
    return result;
  }
  
  // En producción, usar la función serverless con reintentos
  let lastError = null;
  
  for (let attempt = 1; attempt <= EMAIL_CONFIG.MAX_RETRIES; attempt++) {
    try {
      console.log(`📧 Intento ${attempt}/${EMAIL_CONFIG.MAX_RETRIES}:`, {
        requestId,
        attempt,
        timestamp: new Date().toISOString()
      });
      
      const functionUrl = envConfig.functionUrl;
      
      // Crear controlador de timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), EMAIL_CONFIG.TIMEOUT);
      
      try {
        // Enviar solicitud a la función serverless
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to,
            subject,
            html
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        let result;
        try {
          result = await response.json();
        } catch (jsonErr) {
          result = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        if (!response.ok) {
          const detailMsg = result.details
            ? `${result.error || 'Error'}: ${result.details}`
            : (result.error || `HTTP ${response.status}: ${response.statusText}`);
          throw new Error(detailMsg);
        }
        
        const success = {
          success: true,
          message: 'Correo enviado exitosamente',
          requestId,
          attempt,
          duration: Date.now() - startTime,
          mode: 'production'
        };
        
        console.log('✅ Correo enviado exitosamente:', success);
        return success;
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        throw fetchError;
      }
      
    } catch (error) {
      lastError = error;
      
      console.warn(`⚠️ Intento ${attempt} fallido:`, {
        requestId,
        attempt,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      
      // Si no es el último intento, esperar antes del siguiente
      if (attempt < EMAIL_CONFIG.MAX_RETRIES) {
        const delay = EMAIL_CONFIG.RETRY_DELAY * attempt; // Backoff exponencial
        console.log(`⏳ Esperando ${delay}ms antes del siguiente intento...`);
        await sleep(delay);
      }
    }
  }
  
  // Todos los intentos fallaron
  const finalError = {
    success: false,
    errors: [lastError?.message || 'Error desconocido'],
    message: `Falló después de ${EMAIL_CONFIG.MAX_RETRIES} intentos`,
    requestId,
    duration: Date.now() - startTime,
    lastError: lastError?.message
  };
  
  console.error('❌ Envío de email falló completamente:', finalError);
  return finalError;
});

/**
 * Envía una notificación de creación y asignación de acta
 * @param {string} to - Correo del responsable
 * @param {Object} acta - Datos del acta creada
 * @returns {Promise<Object>} - Resultado del envío con detalles
 */
export const sendActaCreationNotification = withMetrics(async (to, acta) => {
  try {
    // Validar datos de entrada
    if (!acta?.numeroActa) {
      return {
        success: false,
        errors: ['Número de acta requerido'],
        message: 'Datos de acta inválidos'
      };
    }
    
    const subject = `[IETS GESTOR ACTAS] Nueva acta creada y asignada: ${acta.numeroActa}`;
    
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1976d2;">Nueva Acta Creada y Asignada</h2>
        <p>Estimado/a líder,</p>
        <p>Se ha creado una nueva acta <strong>${acta.numeroActa}</strong> del proyecto <strong>${acta.tipoProyecto || 'No especificado'}</strong> 
           y ha sido asignada a su responsabilidad.</p>
        
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Detalles del Acta:</h3>
          <ul>
            <li><strong>Número:</strong> ${acta.numeroActa}</li>
            <li><strong>Proyecto:</strong> ${acta.tipoProyectos && acta.tipoProyectos.length > 0 ? acta.tipoProyectos.join(', ') : acta.tipoProyecto || 'No especificado'}</li>
            <li><strong>Producto:</strong> ${acta.productosProyecto && acta.productosProyecto.length > 0 ? acta.productosProyecto.join(', ') : acta.productoProyecto || 'No especificado'}</li>
            <li><strong>Fecha:</strong> ${acta.fecha ? (acta.fecha.toDate ? acta.fecha.toDate().toLocaleDateString('es-ES') : new Date(acta.fecha).toLocaleDateString('es-ES')) : 'No especificada'}</li>
            <li><strong>Aprobador:</strong> ${acta.customProjectEmail || to || 'No asignado'}</li>
            <li><strong>Estado:</strong> ${acta.approvalStatus || 'Por aprobar'}</li>
          </ul>
        </div>
        
        <p>Por favor, ingrese al sistema de gestión de actas para revisar y aprobar el acta asignada:</p>
        
        <div style="text-align: center; margin: 25px 0;">
          <a href="https://gestionactas.iets.org.co" style="background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">🔗 Acceder al Sistema de Gestión de Actas</a>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
          <p>Este es un correo automático, por favor no responda a este mensaje.</p>
          <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
        </div>
      </div>
    `;
    
    const result = await sendEmail(to, subject, html);
    console.log('📧 Resultado del envío de notificación de creación:', {
      success: result.success,
      actaNumero: acta.numeroActa,
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      timestamp: new Date().toISOString()
    });
    
    return result;
    
  } catch (error) {
    const errorResult = {
      success: false,
      errors: [error.message],
      message: 'Error al enviar notificación de creación',
      timestamp: new Date().toISOString()
    };
    
    console.error('❌ Error en sendActaCreationNotification:', errorResult);
    return errorResult;
  }
});

/**
 * Envía una notificación de acta aprobada al creador
 * @param {string} to - Correo del creador del acta
 * @param {Object} acta - Datos del acta aprobada
 * @param {string} approvedBy - Nombre del usuario que aprobó
 * @returns {Promise<Object>} - Resultado del envío con detalles
 */
export const sendActaApprovedNotification = withMetrics(async (to, acta, approvedBy) => {
  try {
    // Validar datos de entrada
    if (!acta?.numeroActa) {
      return {
        success: false,
        errors: ['Número de acta requerido'],
        message: 'Datos de acta inválidos'
      };
    }
    
    console.log('📧 Iniciando envío de notificación de aprobación:', {
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      actaNumero: acta.numeroActa,
      approvedBy: approvedBy || 'No especificado',
      timestamp: new Date().toISOString()
    });
    
    const subject = `[IETS GESTOR ACTAS] Acta aprobada: ${acta.numeroActa}`;
    
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #4caf50;">✅ Acta Aprobada</h2>
        <p>Estimado/a creador/a,</p>
        <p>Nos complace informarle que el acta <strong>${acta.numeroActa}</strong> del proyecto <strong>${acta.tipoProyecto || 'No especificado'}</strong> 
           ha sido <strong style="color: #4caf50;">aprobada</strong>.</p>
        
        <div style="background-color: #e8f5e8; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #4caf50;">
          <h3 style="margin-top: 0; color: #2e7d32;">Detalles del Acta Aprobada:</h3>
          <ul style="margin: 10px 0;">
            <li><strong>Número:</strong> ${acta.numeroActa}</li>
            <li><strong>Proyecto:</strong> ${acta.tipoProyectos && acta.tipoProyectos.length > 0 ? acta.tipoProyectos.join(', ') : acta.tipoProyecto || 'No especificado'}</li>
            <li><strong>Producto:</strong> ${acta.productosProyecto && acta.productosProyecto.length > 0 ? acta.productosProyecto.join(', ') : acta.productoProyecto || 'No especificado'}</li>
            <li><strong>Fecha del acta:</strong> ${acta.fecha ? (acta.fecha.toDate ? acta.fecha.toDate().toLocaleDateString('es-ES') : new Date(acta.fecha).toLocaleDateString('es-ES')) : 'No especificada'}</li>
            <li><strong>Aprobado por:</strong> ${approvedBy || 'Usuario del sistema'}</li>
            <li><strong>Fecha de aprobación:</strong> ${new Date().toLocaleDateString('es-ES')}</li>
          </ul>
        </div>
        
        <div style="background-color: #f0f8ff; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 0; font-weight: bold; color: #1976d2;">🎉 ¡Felicitaciones!</p>
          <p style="margin: 5px 0 0 0;">Su acta ha completado exitosamente el proceso de revisión y aprobación.</p>
        </div>
        
        <p>Puede acceder al sistema de gestión de actas para ver el estado actualizado y descargar la versión final del documento.</p>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
          <p>Este es un correo automático, por favor no responda a este mensaje.</p>
          <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
        </div>
      </div>
    `;
    
    const result = await sendEmail(to, subject, html);
    console.log('📧 Resultado del envío de notificación de aprobación:', {
      success: result.success,
      actaNumero: acta.numeroActa,
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      timestamp: new Date().toISOString()
    });
    
    return result;
    
  } catch (error) {
    const errorResult = {
      success: false,
      errors: [error.message],
      message: 'Error al enviar notificación de aprobación',
      timestamp: new Date().toISOString()
    };
    
    console.error('❌ Error en sendActaApprovedNotification:', errorResult);
    return errorResult;
  }
});

/**
 * Envía una notificación de acta pendiente de aprobación
 * @param {string} to - Correo del responsable
 * @param {Object} acta - Datos del acta pendiente
 * @returns {Promise<boolean>} - Indica si el envío fue exitoso
 */
export const sendApprovalNotification = withMetrics(async (to, acta) => {
  try {
    // Validar datos de entrada
    if (!acta?.numeroActa) {
      return {
        success: false,
        errors: ['Número de acta requerido'],
        message: 'Datos de acta inválidos'
      };
    }
    
    console.log('📧 Iniciando envío de notificación de aprobación pendiente:', {
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      actaNumero: acta.numeroActa,
      timestamp: new Date().toISOString()
    });
  const subject = `[IETS GESTOR ACTAS] Acta pendiente de aprobación: ${acta.numeroActa}`;
  
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #1976d2;">Notificación de Acta Pendiente</h2>
      <p>Estimado/a líder,</p>
      <p>El acta <strong>${acta.numeroActa}</strong> del proyecto <strong>${acta.tipoProyecto}</strong> 
         lleva más de 48 horas pendiente de aprobación.</p>
      
      <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <h3 style="margin-top: 0;">Detalles del Acta:</h3>
        <ul>
          <li><strong>Número:</strong> ${acta.numeroActa}</li>
          <li><strong>Proyecto:</strong> ${acta.tipoProyectos && acta.tipoProyectos.length > 0 ? acta.tipoProyectos.join(', ') : acta.tipoProyecto || 'No especificado'}</li>
          <li><strong>Producto:</strong> ${acta.productosProyecto && acta.productosProyecto.length > 0 ? acta.productosProyecto.join(', ') : acta.productoProyecto || 'No especificado'}</li>
          <li><strong>Fecha:</strong> ${acta.fecha ? (acta.fecha.toDate ? acta.fecha.toDate().toLocaleDateString('es-ES') : new Date(acta.fecha).toLocaleDateString('es-ES')) : 'No especificada'}</li>
          <li><strong>Líder responsable:</strong> ${acta.responsable}</li>
        </ul>
      </div>
      
      <p>Por favor, ingrese al sistema de gestión de actas para revisar y aprobar el acta pendiente:</p>
      
      <div style="text-align: center; margin: 25px 0;">
        <a href="https://gestionactas.iets.org.co" style="background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">🔗 Acceder al Sistema de Gestión de Actas</a>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
        <p>Este es un correo automático, por favor no responda a este mensaje.</p>
        <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
      </div>
    </div>
  `;
  
    const emailResult = await sendEmail(to, subject, html);
    
    console.log('📧 Resultado de notificación de aprobación pendiente:', {
      success: emailResult.success,
      actaNumero: acta.numeroActa,
      to: (typeof to === 'string' ? to.substring(0, 3) : String(to || '').substring(0, 3)) + '***',
      duration: emailResult.duration,
      timestamp: new Date().toISOString()
    });
    
    return emailResult;
    
  } catch (error) {
    const errorResult = {
      success: false,
      errors: [error.message],
      message: 'Error al enviar notificación de aprobación pendiente',
      timestamp: new Date().toISOString()
    };
    
    console.error('❌ Error en sendApprovalNotification:', errorResult);
    return errorResult;
  }
});